<template>
  <div flat multiple class="domain-list-content-wrapper">
    <span v-for="domain in tolldomains" :key="domain.id">
      <v-row v-if="domain.rules.length > 0" class="domain-row mb-4" @click="updatePeriod(domain, domain.rules.pop())">
        <div v-if="!selectedZones" :id="`domain-attribute-name-${domain.id}-${domain.name}`" class="domain-attribute">
          <v-chip
            v-if="findVariantName(domain.variantId) === 'Default'"
            :id="`domain-attribute-chip-${domain.id}-${domain.name}-default`"
            :color="themeColors.secondary"
            dark
            small
          >
            {{ $t('common.default') }}
          </v-chip>
          <div v-else :id="`domain-attribute-chip-${domain.id}-${domain.name}-${findVariantName(domain.variantId)}`">
            {{ findVariantName(domain.variantId) }}
          </div>
        </div>
        <div :id="`domain-attribute-name-${domain.id}-${domain.name}`" class="domain-attribute"> {{ domain.name }} </div>
        <div :id="`domain-attribute-valid-from-${domain.id}-${domain.name}`" class="domain-attribute"> {{ formatDate(domain.startTs) }} </div>
        <div :id="`domain-attribute-valid-to-${domain.id}-${domain.name}`" class="domain-attribute"> {{ formatDate(domain.endTs) }} </div>
        <div :id="`domain-attribute-status-${domain.id}-${domain.name}`" class="domain-attribute">
          <v-chip
            v-if="isPast(domain.startTs, domain.endTs)"
            :id="`domain-attribute-chip-${domain.id}-${domain.name}-past`"
            :color="themeColors.purple"
            dark
            small
          >{{ $t('common.past') }}</v-chip>
          <v-chip
            v-else-if="isOngoing(domain.startTs, domain.endTs)"
            :id="`domain-attribute-chip-${domain.id}-${domain.name}-current`"
            :color="themeColors.secondary"
            dark
            small
          >{{ $t('common.current') }}</v-chip>
          <v-chip
            v-else
            :id="`domain-attribute-chip-${domain.id}-${domain.name}-future`"
            :color="themeColors.green"
            dark
            small
          >{{ $t('common.future') }}</v-chip>
        </div>
        <div v-if="!selectedZones" :id="`domain-attribute-modified-by-${domain.id}-${domain.name}`" class="domain-attribute"> {{ domain.modifiedBy }}</div>
      </v-row>
    </span>
  </div>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import { INavigation, store } from '@/store/modules/tollDomain/types';
import { store as userStore } from '@/store/modules/user/types';
import { colors } from '@/constants/theme/colors';
import './styles/domains-list-table.scss';
import Utils from '@/mixins/Utils';
import { Zone } from 'ctp-geometry';
import { TollDomain, Variant } from 'ctp-toll-domain';

@Component
export default class DomainsListTable extends Mixins(Utils) {
  @Prop()
  private selectedZones: Zone[];

  @Prop()
  private variant: number;

  @Prop()
  private showObsolete: boolean;

  private themeColors = colors;

  @userStore.Getter
  private getSelectedMasterAccount: MasterAccountInfo;

  @store.Action
  private fetchVariants: () => Promise<void>;

  @store.Getter
  private getVariants: Variant[];

  @store.Action
  private fetchTollDomains!: (payload: any) => Promise<void>;

  @store.Action
  private fetchTotalTollDomains!: (payload: any) => Promise<void>;

  @store.Getter
  private getNavigationConfiguration!: INavigation;

  @store.Getter
  private getTotal!: number;

  @store.Getter
  private getTollDomains!: any;

  @Watch('getNavigationConfiguration.start', { immediate: true })
  private async onNavigationChanged() {
    const payload: any = { ...this.getNavigationConfiguration };
    payload.hideObsolete = !this.showObsolete;
    if (this.variant && this.variant > 0) payload.variant = this.variant;
    await Promise.all([this.fetchTollDomains(payload), this.fetchTotalTollDomains({ variant: this.variant, hideObsolete: !this.showObsolete })]);
  }

  @Watch('showObsolete', { immediate: true })
  private async onShowObsoleteChanged() {
    const payload: any = { ...this.getNavigationConfiguration };
    payload.hideObsolete = !this.showObsolete;
    if (this.variant && this.variant > 0) payload.variant = this.variant;
    await Promise.all([this.fetchTollDomains(payload), this.fetchTotalTollDomains({ variant: this.variant, hideObsolete: !this.showObsolete })]);
  }

  @Watch('getSelectedMasterAccount', { immediate: true, deep: true })
  private async onMasterChange() {
    const account = this.getSelectedMasterAccount;
    if (account.name) {
      const payload: any = { ...this.getNavigationConfiguration };
      payload.hideObsolete = !this.showObsolete;
      if (this.variant && this.variant > 0) payload.variant = this.variant;
      await Promise.all([this.fetchTollDomains(payload), this.fetchTotalTollDomains({ variant: this.variant, hideObsolete: !this.showObsolete })]);
      await this.fetchVariants();
    }
  }

  @Watch('variant', { immediate: true })
  private async onVariantChange() {
    this.queryTollDomains();
  }

  private mounted() {
    if (this.selectedZones) {
      this.queryTollDomains();
    }
  }

  private get tolldomains() {
    if (this.selectedZones) {
      const result: TollDomain[] = [];
      this.selectedZones.forEach((zone: Zone) => {
        this.getTollDomains.forEach((tolldomain: TollDomain) => {
          if (zone.name === tolldomain.name && tolldomain.variantId === this.variant) {
            result.push(tolldomain);
          }
        });
      });
      return result;
    }
    return this.getTollDomains;
  }

  private updatePeriod(domain: any, rule: any) {
    this.$router.push({
      name: 'tolldomains-rules-update',
      query: {
        id: domain.id,
        ruleId: rule.id
      }
    });
  }

  private findVariantName(variantId: number) {
    const variant = this.getVariants.find(v => v.id === variantId);
    return variant ? variant.name : '';
  }

  private async queryTollDomains() {
    await this.fetchTotalTollDomains({ variant: this.variant, hideObsolete: !this.showObsolete });
    const payload: any = {
      start: 0,
      domainsPerPage: this.getTotal,
      direction: 'forward',
      isLastDomainPage: false,
      hideObsolete: !this.showObsolete
    };
    if (this.variant && this.variant > 0) payload.variant = this.variant;
    this.fetchTollDomains(payload);
  }
}
</script>
